import React, { useState, useEffect } from 'react';
import { ApiService } from '../services/api';

interface ShareSessionModalProps {
  sessionId: string;
  sessionName: string;
  currentSharedWith?: Array<{ teacherId: string; teacherName: string; sharedAt: string }>;
  onClose: () => void;
  onUpdate: () => void;
}

export const ShareSessionModal: React.FC<ShareSessionModalProps> = ({
  sessionId,
  sessionName,
  currentSharedWith = [],
  onClose,
  onUpdate
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<{ id: string; name: string; email: string } | null>(null);
  const [teacherEmail, setTeacherEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [sharedWith, setSharedWith] = useState(currentSharedWith);

  // Rechercher des enseignants
  useEffect(() => {
    const searchTeachers = async () => {
      if (searchQuery.length >= 2) {
        try {
          const response = await ApiService.searchTeachers(searchQuery);
          if (response.success) {
            setSearchResults(response.teachers);
          }
        } catch (err: any) {
          console.error('Erreur recherche enseignants:', err);
        }
      } else {
        setSearchResults([]);
      }
    };

    const timeoutId = setTimeout(searchTeachers, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Charger la liste actuelle des partages
  useEffect(() => {
    const loadSharedWith = async () => {
      try {
        const response = await ApiService.getSessionSharedWith(sessionId);
        if (response.success) {
          setSharedWith(response.sharedWith);
        }
      } catch (err: any) {
        console.error('Erreur chargement partages:', err);
      }
    };
    loadSharedWith();
  }, [sessionId]);

  const handleShare = async () => {
    if (!selectedTeacher && !teacherEmail.trim()) {
      setError('Veuillez sélectionner un enseignant ou entrer un email');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = selectedTeacher
        ? await ApiService.shareSession(sessionId, undefined, selectedTeacher.id)
        : await ApiService.shareSession(sessionId, teacherEmail.trim());

      if (response.success) {
        setSuccess(response.message);
        setSelectedTeacher(null);
        setTeacherEmail('');
        setSearchQuery('');
        setSearchResults([]);
        
        // Recharger la liste des partages
        const sharedResponse = await ApiService.getSessionSharedWith(sessionId);
        if (sharedResponse.success) {
          setSharedWith(sharedResponse.sharedWith);
        }
        
        onUpdate();
        
        // Effacer le message de succès après 3 secondes
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du partage de la session');
    } finally {
      setLoading(false);
    }
  };

  const handleUnshare = async (teacherId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer le partage avec cet enseignant ?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await ApiService.unshareSession(sessionId, teacherId);
      if (response.success) {
        setSharedWith(response.sharedWith);
        onUpdate();
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du retrait du partage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(2px)'
    }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}>
        <div className="modal-header" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: '1px solid #e9ecef'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>Partager la session : {sessionName}</h2>
          <button 
            className="close-button" 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '2rem',
              cursor: 'pointer',
              color: '#6c757d',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
              e.currentTarget.style.color = '#333';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6c757d';
            }}
          >
            &times;
          </button>
        </div>

        <div className="modal-body" style={{
          padding: '1.5rem'
        }}>
          {error && (
            <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
              {success}
            </div>
          )}

          {/* Recherche d'enseignants */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Rechercher un enseignant :
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nom, email ou ID..."
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            />

            {/* Résultats de recherche */}
            {searchResults.length > 0 && (
              <div style={{
                marginTop: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                maxHeight: '200px',
                overflow: 'auto',
                backgroundColor: '#fff'
              }}>
                {searchResults.map((teacher) => (
                  <div
                    key={teacher.id}
                    onClick={() => {
                      setSelectedTeacher(teacher);
                      setSearchQuery(`${teacher.name} (${teacher.email})`);
                      setSearchResults([]);
                    }}
                    style={{
                      padding: '0.75rem',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      backgroundColor: selectedTeacher?.id === teacher.id ? '#e3f2fd' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = selectedTeacher?.id === teacher.id ? '#e3f2fd' : 'transparent';
                    }}
                  >
                    <strong>{teacher.name}</strong>
                    <br />
                    <small style={{ color: '#666' }}>{teacher.email}</small>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ou entrer un email directement */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Ou entrer l'email de l'enseignant :
            </label>
            <input
              type="email"
              value={teacherEmail}
              onChange={(e) => {
                setTeacherEmail(e.target.value);
                setSelectedTeacher(null);
                setSearchQuery('');
              }}
              placeholder="email@exemple.com"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Bouton de partage */}
          <button
            onClick={handleShare}
            disabled={loading || (!selectedTeacher && !teacherEmail.trim())}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '1rem',
              cursor: loading || (!selectedTeacher && !teacherEmail.trim()) ? 'not-allowed' : 'pointer',
              opacity: loading || (!selectedTeacher && !teacherEmail.trim()) ? 0.6 : 1
            }}
          >
            {loading ? 'Partage en cours...' : 'Partager la session'}
          </button>

          {/* Liste des enseignants avec qui la session est partagée */}
          {sharedWith.length > 0 && (
            <div style={{ marginTop: '2rem', borderTop: '1px solid #ddd', paddingTop: '1rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Partagée avec :</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {sharedWith.map((shared) => (
                  <div
                    key={shared.teacherId}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.75rem',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      border: '1px solid #dee2e6'
                    }}
                  >
                    <div>
                      <strong>{shared.teacherName}</strong>
                      <br />
                      <small style={{ color: '#666' }}>
                        Partagée le {new Date(shared.sharedAt).toLocaleDateString()}
                      </small>
                    </div>
                    <button
                      onClick={() => handleUnshare(shared.teacherId)}
                      disabled={loading}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Retirer
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

